/** Longest edge of the stored avatar, in CSS pixels. Renders crisply at the
 *  largest place we show it (96px at 2x) with headroom to spare. */
const AVATAR_MAX_EDGE = 512;

/** Server-side cap on the decoded image. Matches MAX_BYTES in
 *  controllers/userAvatarController.js. */
export const AVATAR_MAX_BYTES = 512 * 1024;

/** What a file picker should accept. Anything decodable is re-encoded to JPEG
 *  before upload, so this is wider than what the server stores — notably it
 *  lets an iPhone hand back a HEIC, which Safari can decode. */
export const AVATAR_ACCEPT = 'image/*';

/** Guard the decode step: a 50MP photo would otherwise be decoded in full. */
const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

export class ImageError extends Error {}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new ImageError('That image could not be read.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new ImageError('That image could not be read.'));
    img.src = src;
  });
}

/**
 * Centre-crop any drawable source to a square JPEG data URL small enough to
 * store inline.
 *
 * Cropping to a square here rather than in CSS means every avatar is the same
 * shape wherever it is rendered, and the stored bytes are only the pixels we
 * actually display.
 *
 * @param source   anything drawImage accepts — an HTMLImageElement or a
 *                 playing HTMLVideoElement
 * @param width    intrinsic width of that source
 * @param height   intrinsic height of that source
 * @param options  `mirror` flips the crop, for a selfie-facing camera whose
 *                 preview was shown mirrored
 */
export function cropToSquareDataUrl(source, width, height, { mirror = false } = {}) {
  const side = Math.min(width, height);
  if (!side) throw new ImageError('That image could not be read.');

  const target = Math.min(side, AVATAR_MAX_EDGE);
  const sx = Math.round((width - side) / 2);
  const sy = Math.round((height - side) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = target;
  canvas.height = target;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageError('This browser cannot process images.');
  ctx.imageSmoothingQuality = 'high';
  // Transparent PNGs would otherwise composite onto black in the JPEG.
  ctx.fillStyle = '#14141a';
  ctx.fillRect(0, 0, target, target);

  if (mirror) {
    ctx.translate(target, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(source, sx, sy, side, side, 0, 0, target, target);

  // Step the quality down rather than rejecting a photo for being detailed.
  for (const quality of [0.85, 0.72, 0.6, 0.45]) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    // 4 base64 chars encode 3 bytes; padding is close enough at this scale.
    const bytes = Math.floor(base64.length * 0.75);
    if (bytes <= AVATAR_MAX_BYTES) return dataUrl;
  }

  throw new ImageError('That image is too large. Please choose a smaller one.');
}

/** A file from the picker or the device camera. */
export async function fileToAvatarDataUrl(file) {
  if (!file) throw new ImageError('No image selected.');

  const type = String(file.type || '').toLowerCase();
  if (type && !type.startsWith('image/')) {
    throw new ImageError('That file is not an image.');
  }

  if (file.size > MAX_SOURCE_BYTES) {
    throw new ImageError('That image is too large. Please choose one under 12MB.');
  }

  const img = await loadImage(await readAsDataUrl(file));
  return cropToSquareDataUrl(img, img.naturalWidth, img.naturalHeight);
}

/** A still from a live camera preview. */
export function videoFrameToAvatarDataUrl(video, { mirror = false } = {}) {
  if (!video?.videoWidth) throw new ImageError('The camera is not ready yet.');
  return cropToSquareDataUrl(video, video.videoWidth, video.videoHeight, { mirror });
}

export default fileToAvatarDataUrl;
